import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ClipboardCheck, Loader2, ScanLine, Trash2, ArrowLeft, FileSpreadsheet, Lock, Unlock, CheckCircle2, AlertTriangle, Edit3, CalendarDays, MapPin,
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtMoney = (n) => {
  const x = Number(n) || 0;
  return '₹' + x.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// YYYY-MM-DD ⇄ Date helpers (ISO date only, no timezone shift).
const isoToDate = (iso) => {
  if (!iso) return null;
  const parts = String(iso).split('T')[0].split('-');
  if (parts.length !== 3) return null;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};
const dateToIso = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const fmtDateShort = (iso) => {
  if (!iso) return '';
  const d = isoToDate(iso);
  if (!d) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const computeAgeing = (mfgIso, expIso) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const mfg = isoToDate(mfgIso);
  const exp = isoToDate(expIso);
  const dayMs = 86400000;
  return {
    ageingDays: exp ? Math.round((exp.getTime() - today.getTime()) / dayMs) : null,
    productLife: (mfg && exp) ? Math.round((exp.getTime() - mfg.getTime()) / dayMs) : null,
    consumedLife: mfg ? Math.round((today.getTime() - mfg.getTime()) / dayMs) : null,
  };
};

function DatePickerInline({ value, onChange, placeholder, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const date = isoToDate(value);
  // Year-first → Month → Date selection: dropdown-buttons caption gives the user
  // independent Year + Month dropdowns above the day grid, so picking a date
  // 2-3 years out (typical for expiry) is a single popover interaction.
  const currentYear = new Date().getFullYear();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`h-8 w-full justify-start text-left font-normal text-xs px-2 ${value ? '' : 'text-slate-400'}`}
          data-testid={testid}
        >
          <CalendarDays size={12} className="mr-1 flex-shrink-0" />
          {value ? fmtDateShort(value) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => { onChange(d ? dateToIso(d) : ''); setOpen(false); }}
          captionLayout="dropdown-buttons"
          fromYear={currentYear - 10}
          toYear={currentYear + 15}
          defaultMonth={date || new Date()}
          classNames={{
            caption: 'flex justify-center pt-1 pb-2 relative items-center gap-1',
            caption_dropdowns: 'flex justify-center gap-1',
            dropdown: 'rounded border border-slate-200 bg-white px-2 py-1 text-sm font-medium hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300',
            dropdown_month: 'mr-1',
            dropdown_year: '',
            vhidden: 'sr-only',
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function ScanResultPanel({ result, sessionId, onSaved, locked }) {
  const [physical, setPhysical] = useState({});
  const [mrpEdits, setMrpEdits] = useState({});
  const [mfgEdits, setMfgEdits] = useState({});
  const [expiryEdits, setExpiryEdits] = useState({});
  const [rackEdits, setRackEdits] = useState({});
  const [binEdits, setBinEdits] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (result) {
      const p = {}, m = {}, mfg = {}, exp = {}, r = {}, b = {};
      (result.batches || []).forEach((batch) => {
        const key = batch.batch_id || '_synthetic';
        if (batch.existing_entry) {
          p[key] = String(batch.existing_entry.physical_qty);
          m[key] = String(batch.existing_entry.new_mrp);
          mfg[key] = batch.existing_entry.mfg_date || batch.mfg_date || '';
          exp[key] = batch.existing_entry.expiry_date || batch.expiry_date || '';
          r[key] = batch.existing_entry.rack || batch.rack || '';
          b[key] = batch.existing_entry.bin || batch.bin || '';
        } else {
          mfg[key] = batch.mfg_date || '';
          exp[key] = batch.expiry_date || '';
          r[key] = batch.rack || '';
          b[key] = batch.bin || '';
        }
      });
      setPhysical(p); setMrpEdits(m);
      setMfgEdits(mfg); setExpiryEdits(exp);
      setRackEdits(r); setBinEdits(b);
    }
  }, [result]);

  if (!result) return null;
  const { product, batches } = result;

  // Lines actually filled (have a physical_qty entered).
  const filledKeys = batches
    .map((b) => b.batch_id || '_synthetic')
    .filter((k) => physical[k] !== undefined && physical[k] !== '');

  const saveAll = async () => {
    if (filledKeys.length === 0) {
      toast.error('Enter physical quantity on at least one batch row');
      return;
    }
    // Client-side validation (Expiry > MFG, MRP ≥ 0, qty ≥ 0).
    const lines = [];
    for (const b of batches) {
      const key = b.batch_id || '_synthetic';
      const phyRaw = physical[key];
      if (phyRaw === undefined || phyRaw === '') continue;
      const phy = Number(phyRaw);
      if (!isFinite(phy) || phy < 0) {
        toast.error(`Physical qty must be ≥ 0 (batch ${b.batch_number || 'no-batch'})`);
        return;
      }
      const mrpRaw = mrpEdits[key];
      const newMrp = mrpRaw === undefined || mrpRaw === '' ? b.mrp : Number(mrpRaw);
      if (!isFinite(newMrp) || newMrp < 0) {
        toast.error(`MRP must be ≥ 0 (batch ${b.batch_number || 'no-batch'})`);
        return;
      }
      const mfg = mfgEdits[key] || '';
      const exp = expiryEdits[key] || '';
      if (mfg && exp) {
        const md = isoToDate(mfg);
        const ed = isoToDate(exp);
        if (md && ed && ed <= md) {
          toast.error(`Expiry must be after MFG (batch ${b.batch_number || 'no-batch'})`);
          return;
        }
      }
      lines.push({
        product_id: product.id,
        batch_id: b.batch_id,
        physical_qty: phy,
        mrp: newMrp,
        mfg_date: mfg || null,
        expiry_date: exp || null,
        rack: rackEdits[key] || null,
        bin: binEdits[key] || null,
      });
    }
    setSavingAll(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/stock-audit/sessions/${sessionId}/entries/bulk`,
        { lines },
        { headers: { Authorization: 'Bearer ' + token } },
      );
      const saved = res.data.saved_count || 0;
      const errs = res.data.error_count || 0;
      if (saved > 0 && errs === 0) {
        toast.success(`Saved ${saved} line${saved === 1 ? '' : 's'}`);
      } else if (saved > 0 && errs > 0) {
        toast.warning(`Saved ${saved}, ${errs} failed (${(res.data.errors[0] || {}).reason || 'see logs'})`);
      } else if (errs > 0) {
        toast.error(`Save failed for all ${errs} line${errs === 1 ? '' : 's'} — ${(res.data.errors[0] || {}).reason || 'unknown'}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <Card className="border-violet-200 bg-violet-50/40" data-testid="scan-result-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="text-violet-700 bg-violet-100 rounded-lg p-2"><ScanLine size={20} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-800">{product.name}</div>
            <div className="text-xs text-slate-500 font-mono">
              {product.product_id} · {product.barcode || 'no-barcode'} · {product.brand} · {product.category}
            </div>
          </div>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            Aggregate Stock: {product.current_stock_aggregate}
          </Badge>
        </div>

        <div className="space-y-3">
          {batches.map((b) => {
            const key = b.batch_id || '_synthetic';
            const phyVal = physical[key] !== undefined ? physical[key] : '';
            const mrpVal = mrpEdits[key] !== undefined ? mrpEdits[key] : '';
            const variance = phyVal === '' ? 0 : Number(phyVal) - b.system_qty;
            const mfg = mfgEdits[key] || '';
            const exp = expiryEdits[key] || '';
            const ageing = computeAgeing(mfg, exp);
            return (
              <div key={key} className="border rounded-lg bg-white p-3 space-y-2" data-testid={`batch-row-${key}`}>
                {/* Row 1 — core quantities */}
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-slate-500">Batch</label>
                    <div className="font-mono text-xs text-slate-700 truncate">
                      {b.batch_number || <span className="text-slate-400 italic">no-batch</span>}
                      {b.already_audited && <span className="ml-1 text-[10px] text-emerald-600">✓ saved</span>}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-slate-500">System Qty</label>
                    <div className="text-right font-bold text-slate-700 text-sm h-8 flex items-center justify-end">{b.system_qty}</div>
                  </div>
                  <div className="col-span-3">
                    <label className="text-[10px] uppercase font-semibold text-slate-500">Physical Qty</label>
                    <Input
                      type="number" min={0} step="any"
                      value={phyVal}
                      onChange={(e) => setPhysical({ ...physical, [key]: e.target.value })}
                      disabled={locked}
                      className="h-8"
                      placeholder="Count"
                      data-testid={`physical-qty-${key}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="text-[10px] uppercase font-semibold text-slate-500">MRP</label>
                    <Input
                      type="number" min={0} step="any"
                      value={mrpVal}
                      onChange={(e) => setMrpEdits({ ...mrpEdits, [key]: e.target.value })}
                      disabled={locked}
                      className="h-8"
                      placeholder={`Keep ${b.mrp}`}
                      data-testid={`mrp-edit-${key}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-slate-500">Variance</label>
                    <div className={'text-right font-bold text-sm h-8 flex items-center justify-end ' + (variance > 0 ? 'text-emerald-700' : variance < 0 ? 'text-red-600' : 'text-slate-500')}>
                      {phyVal === '' ? '—' : variance.toFixed(2)}
                    </div>
                  </div>
                </div>
                {/* Row 2 — batch detail (MFG/Expiry/Ageing) + location (Rack/Bin) */}
                <div className="grid grid-cols-12 gap-2 items-end pt-2 border-t border-dashed">
                  <div className="col-span-3">
                    <label className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1"><CalendarDays size={10} /> MFG Date</label>
                    <DatePickerInline
                      value={mfg}
                      onChange={(v) => setMfgEdits({ ...mfgEdits, [key]: v })}
                      placeholder="Pick MFG date"
                      disabled={locked}
                      testid={`mfg-${key}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1"><CalendarDays size={10} /> Expiry Date</label>
                    <DatePickerInline
                      value={exp}
                      onChange={(v) => setExpiryEdits({ ...expiryEdits, [key]: v })}
                      placeholder="Pick expiry date"
                      disabled={locked}
                      testid={`expiry-${key}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-slate-500">Ageing</label>
                    <div
                      className={
                        'text-xs font-semibold h-8 flex items-center justify-center rounded border px-2 ' +
                        (ageing.ageingDays === null
                          ? 'text-slate-400 border-slate-200'
                          : ageing.ageingDays < 0
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : ageing.ageingDays < 30
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200')
                      }
                      data-testid={`ageing-${key}`}
                    >
                      {ageing.ageingDays === null
                        ? '—'
                        : ageing.ageingDays < 0
                          ? `Expired ${Math.abs(ageing.ageingDays)}d ago`
                          : `${ageing.ageingDays} days remaining`}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1"><MapPin size={10} /> Rack</label>
                    <Input
                      value={rackEdits[key] || ''}
                      onChange={(e) => setRackEdits({ ...rackEdits, [key]: e.target.value })}
                      disabled={locked}
                      placeholder="e.g. R-A1"
                      className="h-8 text-xs"
                      data-testid={`rack-${key}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1"><MapPin size={10} /> Bin</label>
                    <Input
                      value={binEdits[key] || ''}
                      onChange={(e) => setBinEdits({ ...binEdits, [key]: e.target.value })}
                      disabled={locked}
                      placeholder="e.g. B-12"
                      className="h-8 text-xs"
                      data-testid={`bin-${key}`}
                    />
                  </div>
                </div>
                {ageing.productLife !== null && (
                  <div className="text-[10px] text-slate-500 pt-1">
                    Product Life: <strong>{ageing.productLife}d</strong> · Consumed Life: <strong>{ageing.consumedLife}d</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Single "Save All" footer */}
        {!locked && (
          <div className="flex items-center justify-between border-t pt-3 mt-2">
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-violet-700">{filledKeys.length}</span> of {batches.length} batch line{batches.length === 1 ? '' : 's'} ready to save
            </div>
            <Button
              onClick={saveAll}
              disabled={savingAll || filledKeys.length === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-1 px-5"
              data-testid="audit-save-all-btn"
            >
              {savingAll ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Save All Lines
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function StockAuditSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/stock-audit/sessions/${id}`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      setSession(res.data.session);
      setEntries(res.data.entries || []);
    } catch (e) {
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { scanInputRef.current?.focus(); }, [scanResult]);

  const locked = session?.status === 'submitted';

  const handleScan = async () => {
    const code = scanCode.trim();
    if (!code) return;
    setScanLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/stock-audit/scan`, {
        headers: { Authorization: 'Bearer ' + token },
        params: { code, session_id: id },
      });
      setScanResult(res.data);
      setScanCode('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Remove this audit line?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/stock-audit/sessions/${id}/entries/${entryId}`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      toast.success('Line removed');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/stock-audit/sessions/${id}/submit`, {}, {
        headers: { Authorization: 'Bearer ' + token },
      });
      toast.success(
        `Audit submitted · ${res.data.adjustments_applied} stock adjustments, ${res.data.mrp_updates} MRP updates applied`
      );
      setShowSubmitConfirm(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/stock-audit/sessions/${id}/variance-report.xlsx`, {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_audit_variance_${session?.audit_number || id}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Variance report downloaded');
    } catch (e) {
      toast.error('Download failed');
    }
  };

  if (loading && !session) return (
    <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
  );
  if (!session) return null;

  return (
    <div className="container mx-auto p-6 space-y-5" data-testid="audit-session-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stock-audit')} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-violet-600" />
              <span className="font-mono text-violet-700">{session.audit_number}</span>
              {locked ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><Lock size={11} /> Locked</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1"><Unlock size={11} /> Open</Badge>
              )}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {session.warehouse} · Created by {session.created_by_name} · {(session.created_at || '').replace('T', ' ').split('.')[0]}
              {session.notes && <> · {session.notes}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadReport}
            className="gap-1 border-violet-300 text-violet-700"
            data-testid="audit-download-report-btn"
          >
            <FileSpreadsheet size={14} /> Variance Report
          </Button>
          <CsvDownloadButton onDownload={handleDownloadReport} testId="audit-download-report-csv" />
          {!locked && (
            <Button
              onClick={() => setShowSubmitConfirm(true)}
              disabled={entries.length === 0 || submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
              data-testid="audit-submit-btn"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Submit &amp; Apply Adjustments
            </Button>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="session-totals">
        <Card className="border"><CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Lines Audited</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">{session.totals?.entries_count || 0}</div>
        </CardContent></Card>
        <Card className="border border-emerald-200 bg-emerald-50"><CardContent className="p-3">
          <div className="text-[10px] uppercase text-emerald-700 font-semibold">Excess Qty</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{session.totals?.excess_qty || 0}</div>
        </CardContent></Card>
        <Card className="border border-red-200 bg-red-50"><CardContent className="p-3">
          <div className="text-[10px] uppercase text-red-700 font-semibold">Shortage Qty</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{session.totals?.shortage_qty || 0}</div>
        </CardContent></Card>
        <Card className="border"><CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Variance ₹</div>
          <div className="text-xl font-bold text-violet-700 mt-1">{fmtMoney(session.totals?.variance_value)}</div>
        </CardContent></Card>
      </div>

      {/* Scan input */}
      {!locked && (
        <Card className="border-2 border-violet-300">
          <CardContent className="p-4 flex items-center gap-3">
            <ScanLine className="w-6 h-6 text-violet-600 flex-shrink-0" />
            <Input
              ref={scanInputRef}
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleScan(); }}
              placeholder="Scan barcode OR type Product ID (PROD-XXX) and press Enter"
              className="flex-1 text-base h-10"
              data-testid="audit-scan-input"
              autoFocus
            />
            <Button
              onClick={handleScan}
              disabled={scanLoading || !scanCode.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-1"
              data-testid="audit-scan-btn"
            >
              {scanLoading ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
              Scan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Scan result */}
      <ScanResultPanel result={scanResult} sessionId={id} onSaved={() => { refresh(); }} locked={locked} />

      {/* Audited entries table */}
      <Card className="border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2">
            <ClipboardCheck size={16} className="text-violet-600" />
            Audited Lines ({entries.length})
          </h2>
          {locked && (
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 gap-1">
              <Lock size={10} /> Read-only — session was submitted on {(session.submitted_at || '').replace('T', ' ').split('.')[0]} by {session.submitted_by_name}
            </Badge>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-entries-table">
            <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-right px-3 py-2">MRP</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">MFG → Expiry</th>
                <th className="text-center px-3 py-2">Ageing</th>
                <th className="text-left px-3 py-2">Rack / Bin</th>
                <th className="text-right px-3 py-2">System</th>
                <th className="text-right px-3 py-2">Physical</th>
                <th className="text-right px-3 py-2">Variance</th>
                <th className="text-right px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">Audited By</th>
                {!locked && <th className="text-center px-3 py-2">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.length === 0 && (
                <tr><td colSpan={locked ? 11 : 12} className="text-center py-8 text-slate-500 text-sm">
                  Scan a product or enter Product ID to begin auditing.
                </td></tr>
              )}
              {entries.map((e) => {
                const v = Number(e.variance) || 0;
                const cls = v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-600' : 'text-slate-500';
                const ageingDays = e.ageing_days;
                const ageingCls = ageingDays === null || ageingDays === undefined
                  ? 'text-slate-400'
                  : ageingDays < 0
                    ? 'text-red-700 bg-red-50'
                    : ageingDays < 30
                      ? 'text-amber-700 bg-amber-50'
                      : 'text-emerald-700 bg-emerald-50';
                return (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`entry-row-${e.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{e.product_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{e.product_sku}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{e.batch_number || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {e.mrp_changed ? (
                        <span className="text-violet-700 font-semibold">
                          {fmtMoney(e.new_mrp)} <span className="text-[10px] text-slate-400 line-through">{fmtMoney(e.old_mrp)}</span>
                        </span>
                      ) : fmtMoney(e.new_mrp)}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {e.mfg_date || e.expiry_date ? (
                        <>
                          <div className="text-slate-700">{fmtDateShort(e.mfg_date) || '—'}</div>
                          <div className="text-slate-400 text-[10px]">→ {fmtDateShort(e.expiry_date) || '—'}</div>
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {ageingDays === null || ageingDays === undefined ? (
                        <span className="text-slate-300 text-xs">—</span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${ageingCls}`}>
                          {ageingDays < 0 ? `Expired ${Math.abs(ageingDays)}d` : `${ageingDays}d`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.rack || e.bin ? (
                        <div className="font-mono text-slate-700">
                          {e.rack || '—'} / {e.bin || '—'}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">{e.system_qty}</td>
                    <td className="px-3 py-2 text-right font-semibold">{e.physical_qty}</td>
                    <td className={'px-3 py-2 text-right font-bold ' + cls}>{v > 0 ? '+' : ''}{v}</td>
                    <td className={'px-3 py-2 text-right ' + cls}>{fmtMoney(e.variance_value)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{e.audited_by_name}</td>
                    {!locked && (
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteEntry(e.id)}
                          className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                          data-testid={`delete-entry-${e.id}`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Submit confirmation dialog */}
      <Dialog open={showSubmitConfirm} onOpenChange={(o) => { if (!o) setShowSubmitConfirm(false); }}>
        <DialogContent data-testid="submit-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 size={18} /> Submit Audit & Apply Adjustments?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-800">
              <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle size={14} /> This action is irreversible</div>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                <li>Stock Master will be updated to your physical counts</li>
                <li>A Stock Ledger entry is created for each variance line (auditable trail)</li>
                <li>MRP changes are written to the corresponding batches</li>
                <li>Session will be locked — no further edits allowed</li>
                <li>GRN, PO, and Invoice modules are NOT affected</li>
              </ul>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <div className="text-[10px] uppercase text-blue-700 font-semibold">Lines</div>
                <div className="text-lg font-bold text-blue-700">{session.totals?.entries_count || 0}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="text-[10px] uppercase text-emerald-700 font-semibold">Excess</div>
                <div className="text-lg font-bold text-emerald-700">{session.totals?.excess_qty || 0}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded p-2">
                <div className="text-[10px] uppercase text-red-700 font-semibold">Shortage</div>
                <div className="text-lg font-bold text-red-700">{session.totals?.shortage_qty || 0}</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitConfirm(false)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
              data-testid="audit-submit-confirm-btn"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Yes, Submit &amp; Lock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
